const getEmbedUrl = url => {
  if (!url) return null;

  // YouTube: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID
  const ytMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]+)/
  );
  if (ytMatch) {
    return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&rel=0`;
  }

  // Vimeo: vimeo.com/ID, player.vimeo.com/video/ID
  const vimeoMatch = url.match(
    /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/
  );
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1`;
  }

  return null;
};

const isDirectVideo = url =>
  /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url || '');

const initExhibitionPopup = () => {
  const popup = document.querySelector('.exhibition-popup');
  if (!popup) return;

  const dataEl = popup.querySelector('.exhibition-popup-data');
  if (!dataEl) return;

  let items;
  try {
    items = JSON.parse(dataEl.textContent);
  } catch {
    return;
  }

  const backdrop = popup.querySelector('.exhibition-popup-backdrop');
  const closeBtn = popup.querySelector('.exhibition-popup-close');
  const mainImageWrap = popup.querySelector('.exhibition-popup-main-image');
  let mainImg = popup.querySelector('.exhibition-popup-slide-img');
  const videoWrap = popup.querySelector('.exhibition-popup-video-wrap');
  const iframeEl = popup.querySelector('.exhibition-popup-iframe');
  const videoEl = popup.querySelector('.exhibition-popup-video');
  const thumbsTrack = popup.querySelector('.exhibition-popup-thumbnails-track');
  const titleEl = popup.querySelector('.exhibition-popup-title');
  const descEl = popup.querySelector('.exhibition-popup-description');
  const triggers = document.querySelectorAll('[data-popup-trigger]');

  let currentItem = null;
  let currentSlide = 0;
  let previousFocus = null;
  let imageRequestId = 0;

  const stopVideo = () => {
    videoEl.pause();
    videoEl.removeAttribute('src');
    videoEl.style.display = 'none';
    iframeEl.removeAttribute('src');
    iframeEl.style.display = 'none';
  };

  /**
   * Replace the <img> node so browsers cannot keep a stale painted frame
   * when arrow-navigating quickly (src swaps race / abort).
   */
  const setMainImage = (url, alt = '') => {
    const requestId = ++imageRequestId;

    videoWrap.style.display = 'none';
    stopVideo();

    // Drop the current bitmap immediately so caption never sits on a stale photo.
    if (mainImg) {
      mainImg.removeAttribute('src');
      mainImg.alt = alt;
      mainImg.style.display = '';
    }

    const nextImg = document.createElement('img');
    nextImg.className = 'exhibition-popup-slide-img';
    nextImg.alt = alt;
    nextImg.style.display = '';

    let committed = false;

    const commit = () => {
      if (committed || requestId !== imageRequestId || !mainImageWrap) return;
      committed = true;

      if (mainImg && mainImg.parentNode === mainImageWrap) {
        mainImageWrap.replaceChild(nextImg, mainImg);
      } else if (!nextImg.parentNode) {
        mainImageWrap.insertBefore(nextImg, videoWrap);
      }
      mainImg = nextImg;
    };

    nextImg.addEventListener('load', commit, { once: true });
    nextImg.addEventListener('error', commit, { once: true });
    nextImg.src = url;

    // Cached images may already be complete before listeners attach.
    if (nextImg.complete && nextImg.naturalWidth > 0) {
      commit();
    }
  };

  const setSlide = index => {
    if (!currentItem || !currentItem.slides.length) return;
    currentSlide = index;
    const slide = currentItem.slides[index];

    if (slide.type === 'video') {
      imageRequestId += 1;
      if (mainImg) {
        mainImg.style.display = 'none';
      }
      videoWrap.style.display = '';

      const embedUrl = getEmbedUrl(slide.url);

      if (embedUrl) {
        videoEl.style.display = 'none';
        iframeEl.style.display = '';
        iframeEl.src = embedUrl;
      } else if (isDirectVideo(slide.url)) {
        iframeEl.style.display = 'none';
        iframeEl.removeAttribute('src');
        videoEl.style.display = '';
        videoEl.src = slide.url;
        videoEl.load();
      } else {
        iframeEl.style.display = 'none';
        videoEl.style.display = 'none';
      }
    } else {
      setMainImage(slide.url, slide.alt || '');
    }

    titleEl.textContent = slide.title || '';
    descEl.innerHTML = slide.description || '';

    const thumbs = thumbsTrack.querySelectorAll('.exhibition-popup-thumb');
    thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
      thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
    });
  };

  const buildThumbs = slides => {
    thumbsTrack.innerHTML = '';
    slides.forEach((slide, i) => {
      const btn = document.createElement('button');
      btn.className = 'exhibition-popup-thumb';
      btn.type = 'button';
      btn.setAttribute('aria-label', `Slide ${i + 1}`);

      if (slide.type === 'video') {
        const playIcon = document.createElement('span');
        playIcon.className = 'exhibition-popup-thumb-play';
        playIcon.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><polygon points="6,3 20,12 6,21" fill="currentColor"/></svg>';
        btn.appendChild(playIcon);

        if (slide.thumb) {
          const img = document.createElement('img');
          img.src = slide.thumb;
          img.alt = '';
          img.loading = 'lazy';
          btn.insertBefore(img, playIcon);
        }
      } else {
        const img = document.createElement('img');
        img.src = slide.thumb || slide.url;
        img.alt = slide.alt || '';
        img.loading = 'lazy';
        btn.appendChild(img);
      }

      btn.addEventListener('click', () => setSlide(i));
      thumbsTrack.appendChild(btn);
    });
  };

  const onKeydown = e => {
    if (popup.getAttribute('aria-hidden') === 'true') return;

    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }

    if (!currentItem || !currentItem.slides.length) return;

    // Hold-to-repeat fires many keydowns; each aborted src load caused the glitch.
    if (e.repeat) return;

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      setSlide((currentSlide + 1) % currentItem.slides.length);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setSlide((currentSlide - 1 + currentItem.slides.length) % currentItem.slides.length);
    }
  };

  const open = (index, slideIndex = 0) => {
    const item = items.find(i => i.index === index);
    if (!item || !item.slides.length) return;

    currentItem = item;
    previousFocus = document.activeElement;

    const startSlide = Math.max(0, Math.min(slideIndex, item.slides.length - 1));

    buildThumbs(item.slides);
    setSlide(startSlide);

    popup.setAttribute('aria-hidden', 'false');
    document.body.classList.add('has-exhibition-popup');
    document.addEventListener('keydown', onKeydown);
    closeBtn.focus();
  };

  const close = () => {
    popup.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('has-exhibition-popup');
    document.removeEventListener('keydown', onKeydown);

    imageRequestId += 1;
    stopVideo();

    currentItem = null;
    currentSlide = 0;

    if (previousFocus) {
      previousFocus.focus();
      previousFocus = null;
    }
  };

  triggers.forEach(trigger => {
    const handler = () => {
      const idx = Number.parseInt(trigger.dataset.popupTrigger, 10);
      const slideIdx = Number.parseInt(trigger.dataset.popupSlide || '0', 10);
      open(idx, Number.isNaN(slideIdx) ? 0 : slideIdx);
    };
    trigger.addEventListener('click', handler);
    trigger.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handler();
      }
    });
  });

  closeBtn.addEventListener('click', close);
  backdrop.addEventListener('click', close);
};

export default initExhibitionPopup;
